import React from 'react';
import { translate } from '../i18n';

/**
 * CF-MAINNET-41: top-level React error boundary.
 *
 * Wraps <App /> in main.tsx. Catches rendering errors anywhere in the component
 * tree that would otherwise crash to white-screen-of-death. Shows a truthful
 * fallback UI with explicit guidance + a reload action; logs the error with
 * React component stack to the console for operator diagnosis.
 *
 * NOTE: error boundaries catch render errors + lifecycle errors + constructor
 * errors in child components. They do NOT catch:
 *   - errors inside event handlers (those need try/catch locally)
 *   - errors in async callbacks (those need try/catch locally)
 *   - errors in server-side rendering
 *   - errors thrown by the ErrorBoundary itself
 * For those surfaces, CF-MAINNET-32/35/38/39/40 have already established the
 * fail-closed + console.error + distinct-error-UI patterns.
 */

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        // Update state so next render shows the fallback UI.
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Structured log — matches the fail-loud pattern used across
        // CF-MAINNET-32/35/38/39/40. React's componentStack helps pinpoint which
        // component in the tree raised.
        console.error('🚨 CF-MAINNET-41: ErrorBoundary caught a rendering error', {
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
        });
        this.setState({ errorInfo });
    }

    handleReload = () => {
        // Full reload — safest recovery for an already-broken React tree. We
        // intentionally don't try to recover in-place because the state that
        // caused the error may still be present.
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div
                    className="min-h-screen flex items-center justify-center p-6"
                    style={{ background: 'var(--bg-primary, #070b14)' }}
                >
                    <div className="glass rounded-2xl p-6 max-w-md w-full text-center">
                        <span className="text-5xl">⚠️</span>
                        <h2 className="text-lg font-black mt-4">{translate('error_boundary.title')}</h2>
                        <p className="text-[13px] mt-3 px-2 leading-snug" style={{ color: 'var(--text-secondary, #9ca3af)' }}>
                            {translate('error_boundary.body')}
                        </p>
                        <button
                            onClick={this.handleReload}
                            className="btn-primary px-6 py-3 rounded-xl text-sm font-bold mt-5 w-full"
                        >
                            {translate('error_boundary.reload_btn')}
                        </button>
                        <p className="text-[10px] mt-4 px-4 leading-snug" style={{ color: 'var(--text-muted, #6b7280)' }}>
                            {translate('error_boundary.contact_note')}
                        </p>
                        {this.state.error && (
                            <details className="mt-4 text-left">
                                <summary className="text-[10px] cursor-pointer" style={{ color: 'var(--text-muted, #6b7280)' }}>
                                    {translate('error_boundary.tech_details_summary')}
                                </summary>
                                <pre className="text-[10px] mt-2 p-2 rounded overflow-auto max-h-40"
                                     style={{ background: 'var(--bg-elevated, #0f172a)', color: 'var(--text-muted, #6b7280)' }}>
                                    {this.state.error.message}
                                    {this.state.errorInfo?.componentStack || ''}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
